/**
 * @fileoverview Reusable Accordion Custom Element
 * A flexible accordion component with animated SVG toggle icon
 * 
 * 📡 EVENT SUMMARY:
 * 
 * EVENTS DISPATCHED:
 * - accordion:opened  → Emitted when accordion opens
 * - accordion:closed  → Emitted when accordion closes
 * - accordion:toggled → Emitted when accordion state changes
 * - accordion:ready   → Emitted when component is initialized
 * 
 * EVENT DATA STRUCTURE:
 * - accordion:opened/closed/toggled: { 
 *     isOpen: boolean, 
 *     timestamp: number,
 *     element: HTMLElement,
 *     trigger: HTMLElement,
 *     content: HTMLElement
 *   }
 * - accordion:ready: {
 *     element: HTMLElement,
 *     config: Object
 *   }
 */

export class AccordionElement extends HTMLElement {
  constructor() {
    super();
    
    this.isOpen = false;
    this.elements = {};
    this.config = {
      animationDuration: 300,
      autoOpen: false,
      allowMultiple: true,
      triggerSelector: '[data-accordion-trigger]',
      contentSelector: '[data-accordion-content]',
      iconSelector: '[data-accordion-icon]',
      ...this.getConfigFromAttributes()
    };

    // Bind methods to preserve context
    this.toggle = this.toggle.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);

    this.init();
  }

  /**
   * Get configuration from data attributes
   * @private
   */
  getConfigFromAttributes() {
    const config = {};
    
    // Auto-open configuration
    config.autoOpen = this.hasAttribute('data-auto-open') || this.hasAttribute('open');
    
    // Animation duration
    const duration = this.getAttribute('data-animation-duration');
    if (duration) config.animationDuration = parseInt(duration) || 300;
    
    // Allow multiple open
    config.allowMultiple = this.getAttribute('data-allow-multiple') !== 'false';
    
    // Icon type (plus or caret)
    if (this.closest('.accordion__container') != undefined) {
      config.iconType = this.closest('.accordion__container').getAttribute('data-icon-type') || 'plus';
    } else {
      config.iconType = this.getAttribute('data-icon-type') || 'plus';
    }
    
    // Custom selectors
    const triggerSelector = this.getAttribute('data-trigger-selector');
    if (triggerSelector) config.triggerSelector = triggerSelector;
    
    const contentSelector = this.getAttribute('data-content-selector');
    if (contentSelector) config.contentSelector = contentSelector;
    
    return config;
  }

  /**
   * Initialize the component
   * @private
   */
  init() {
    console.log('🎯 [AccordionElement] Initializing accordion component');
    
    this.cacheElements();
    this.setupAccessibility();
    this.createToggleIcon();
    this.bindEvents();
    this.setInitialState();
    this.dispatchReadyEvent();
  }

  /**
   * Cache DOM elements
   * @private
   */
  cacheElements() {
    // Try to find existing elements first
    this.elements = {
      trigger: this.querySelector(this.config.triggerSelector),
      content: this.querySelector(this.config.contentSelector),
      icon: this.querySelector(this.config.iconSelector)
    };

    // If no trigger found, create default structure
    if (!this.elements.trigger) {
      this.createDefaultStructure();
    }

    // Validate required elements
    if (!this.elements.trigger || !this.elements.content) {
      throw new Error('AccordionElement: Required elements not found (trigger and content are required)');
    }

    console.log('🎯 [AccordionElement] Elements cached', {
      trigger: !!this.elements.trigger,
      content: !!this.elements.content,
      icon: !!this.elements.icon
    });
  }

  /**
   * Create default accordion structure if not provided
   * @private
   */
  createDefaultStructure() {
    const existingContent = this.innerHTML;
    
    this.innerHTML = `
      <div class="accordion__header">
        <button class="accordion__trigger" data-accordion-trigger type="button">
          <span class="accordion__title">Accordion Title</span>
          <div class="accordion__icon" data-accordion-icon>
            <!-- Icon will be inserted here -->
          </div>
        </button>
      </div>
      <div class="accordion__content" data-accordion-content>
        <div class="accordion__body">
          ${existingContent || 'Accordion content goes here'}
        </div>
      </div>
    `;

    // Re-cache elements after creating structure
    this.elements = {
      trigger: this.querySelector(this.config.triggerSelector),
      content: this.querySelector(this.config.contentSelector),
      icon: this.querySelector(this.config.iconSelector)
    };
  }

  /**
   * Create the animated toggle icon
   * @private
   */
  createToggleIcon() {
    if (!this.elements.icon) return;

    if (this.config.iconType === 'caret') {
      this.createCaretIcon();
    } else {
      this.createPlusIcon();
    }
  }

  /**
   * Create the plus/cross icon
   * @private
   */
  createPlusIcon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'accordion__toggle-icon');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '20');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('fill', 'none');
    svg.style.overflow = 'visible';
    svg.style.display = 'block';
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Horizontal line (always visible)
    const horizontalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    horizontalLine.setAttribute('x1', '0');
    horizontalLine.setAttribute('y1', '10');
    horizontalLine.setAttribute('x2', '20');
    horizontalLine.setAttribute('y2', '10');
    horizontalLine.setAttribute('stroke', 'currentColor');
    horizontalLine.setAttribute('stroke-width', '3');
    horizontalLine.setAttribute('stroke-linecap', 'butt');
    horizontalLine.classList.add('horizontal');

    // Vertical line (animates on toggle)
    const verticalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    verticalLine.setAttribute('x1', '10');
    verticalLine.setAttribute('y1', '0');
    verticalLine.setAttribute('x2', '10');
    verticalLine.setAttribute('y2', '20');
    verticalLine.setAttribute('stroke', 'currentColor');
    verticalLine.setAttribute('stroke-width', '3');
    verticalLine.setAttribute('stroke-linecap', 'butt');
    verticalLine.classList.add('vertical');

    svg.appendChild(horizontalLine);
    svg.appendChild(verticalLine);

    // Clear existing content and add SVG
    this.elements.icon.innerHTML = '';
    this.elements.icon.appendChild(svg);

    // Store reference to vertical line for animation
    this.verticalLine = verticalLine;
  }

  /**
   * Create the caret down icon
   * @private
   */
  createCaretIcon() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'accordion__toggle-icon accordion__toggle-icon--caret');
    svg.setAttribute('width', '20');
    svg.setAttribute('height', '12');
    svg.setAttribute('viewBox', '0 0 20 12');
    svg.setAttribute('fill', 'none');
    svg.style.overflow = 'visible';
    svg.style.display = 'block';
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    // Left path
    const leftPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    leftPath.setAttribute('d', 'M17.125 5.72205e-07L19.5 2.4L10 12L7.625 9.6L17.125 5.72205e-07Z');
    leftPath.setAttribute('fill', '#FFA001');
    leftPath.classList.add('caret-left');

    // Right path
    const rightPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    rightPath.setAttribute('d', 'M12.375 9.6L10 12L0.5 2.4L2.875 0L12.375 9.6Z');
    rightPath.setAttribute('fill', '#FFA001');
    rightPath.classList.add('caret-right');

    svg.appendChild(leftPath);
    svg.appendChild(rightPath);

    // Clear existing content and add SVG
    this.elements.icon.innerHTML = '';
    this.elements.icon.appendChild(svg);

    // Store reference to caret paths for animation
    this.caretLeft = leftPath;
    this.caretRight = rightPath;
  }

  /**
   * Setup accessibility features
   * @private
   */
  setupAccessibility() {
    // Generate unique IDs if not present
    if (!this.elements.content.id) {
      this.elements.content.id = `accordion-content-${Date.now()}`;
    }
    if (!this.elements.trigger.id) {
      this.elements.trigger.id = `accordion-trigger-${Date.now()}`;
    }

    // Set ARIA attributes
    this.elements.trigger.setAttribute('aria-expanded', 'false');
    this.elements.trigger.setAttribute('aria-controls', this.elements.content.id);
    this.elements.trigger.setAttribute('role', 'button');
    
    // Add role to content
    this.elements.content.setAttribute('role', 'region');
    this.elements.content.setAttribute('aria-labelledby', this.elements.trigger.id);
    
    // Ensure trigger is focusable
    if (!this.elements.trigger.hasAttribute('tabindex')) {
      this.elements.trigger.setAttribute('tabindex', '0');
    }
  }

  /**
   * Bind event listeners
   * @private
   */
  bindEvents() {
    console.log('🎯 [AccordionElement] Binding events');

    // Click event for trigger
    this.elements.trigger.addEventListener('click', this.toggle);

    // Keyboard events
    this.elements.trigger.addEventListener('keydown', this.handleKeydown);

    // Focus management
    this.elements.trigger.addEventListener('focus', () => {
      this.classList.add('focused');
    });

    this.elements.trigger.addEventListener('blur', () => {
      this.classList.remove('focused');
    });
  }

  /**
   * Set initial state based on configuration
   * @private
   */
  setInitialState() {
    if (this.config.autoOpen) {
      this.open(true);
    } else {
      this.close();
    }
    
    // Ensure vertical line is properly positioned after state is set
    if (this.verticalLine) {
      if (this.isOpen) {
        this.verticalLine.style.opacity = '0';
        this.verticalLine.style.transform = 'rotate(90deg)';
      } else {
        this.verticalLine.style.opacity = '1';
        this.verticalLine.style.transform = 'rotate(0deg)';
      }
    }
  }

  /**
   * Dispatch ready event
   * @private
   */
  dispatchReadyEvent() {
    this.dispatchEvent(new CustomEvent('accordion:ready', {
      detail: {
        element: this,
        config: this.config
      },
      bubbles: true
    }));
  }

  /**
   * Handle keyboard events
   * @param {KeyboardEvent} event
   * @private
   */
  handleKeydown(event) {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggle();
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.close();
          this.elements.trigger.focus();
        }
        break;
    }
  }

  /**
   * Handle clicks outside the accordion (if needed for closing)
   * @param {Event} event
   * @private
   */
  handleClickOutside(event) {
    // This can be implemented if needed for specific use cases
  }

  /**
   * Toggle accordion state
   * @param {Event} event - Optional event object
   */
  toggle(event) {
    if (event) {
      event.preventDefault();
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }

    // Dispatch toggle event
    this.dispatchEvent(new CustomEvent('accordion:toggled', {
      detail: {
        isOpen: this.isOpen,
        timestamp: Date.now(),
        element: this,
        trigger: this.elements.trigger,
        content: this.elements.content
      },
      bubbles: true
    }));
  }

  /**
   * Open the accordion
   */
  open(instant) {
    if (this.isOpen) return;

    console.log('🎯 [AccordionElement] Opening accordion');

    this.isOpen = true;
    this.classList.add('is-open');
    this.classList.remove('is-closed');

    // Update ARIA attributes
    this.elements.trigger.setAttribute('aria-expanded', 'true');

    if (!instant) {
      // Animate content
      this.animateContent(true);
    }

    // Animate icon
    this.animateIcon(true);

    // Dispatch open event
    this.dispatchEvent(new CustomEvent('accordion:opened', {
      detail: {
        isOpen: this.isOpen,
        timestamp: Date.now(),
        element: this,
        trigger: this.elements.trigger,
        content: this.elements.content
      },
      bubbles: true
    }));
  }

  /**
   * Close the accordion
   */
  close() {
    if (!this.isOpen) return;

    console.log('🎯 [AccordionElement] Closing accordion');

    this.isOpen = false;
    this.classList.remove('is-open');
    this.classList.add('is-closed');

    // Update ARIA attributes
    this.elements.trigger.setAttribute('aria-expanded', 'false');

    // Animate content
    this.animateContent(false);

    // Animate icon
    this.animateIcon(false);

    // Dispatch close event
    this.dispatchEvent(new CustomEvent('accordion:closed', {
      detail: {
        isOpen: this.isOpen,
        timestamp: Date.now(),
        element: this,
        trigger: this.elements.trigger,
        content: this.elements.content
      },
      bubbles: true
    }));
  }

  /**
   * Animate content visibility
   * @param {boolean} isOpening - Whether the accordion is opening
   * @private
   */
  animateContent(isOpening) {
    const content = this.elements.content;
    
    if (isOpening) {
      // Get the natural height
      content.style.height = 'auto';
      const height = content.scrollHeight;
      content.style.height = '0px';
      
      // Force reflow
      content.offsetHeight;
      
      // Animate to natural height
      content.style.transition = `height ${this.config.animationDuration}ms ease-out`;
      content.style.height = height + 'px';
      
      // Clean up after animation
      setTimeout(() => {
        content.style.height = 'auto';
        content.style.transition = '';
      }, this.config.animationDuration);
      
    } else {
      // Set current height
      content.style.height = content.scrollHeight + 'px';
      
      // Force reflow
      content.offsetHeight;
      
      // Animate to 0
      content.style.transition = `height ${this.config.animationDuration}ms ease-out`;
      content.style.height = '0px';
      
      // Clean up after animation
      setTimeout(() => {
        content.style.transition = '';
      }, this.config.animationDuration);
    }
  }

  /**
   * Animate the toggle icon
   * @param {boolean} isOpening - Whether the accordion is opening
   * @private
   */
  animateIcon(isOpening) {
    if (this.config.iconType === 'caret') {
      this.animateCaretIcon(isOpening);
    } else {
      this.animatePlusIcon(isOpening);
    }
  }

  /**
   * Animate the plus/cross icon
   * @param {boolean} isOpening - Whether the accordion is opening
   * @private
   */
  animatePlusIcon(isOpening) {
    if (!this.verticalLine) return;

    // Force a reflow to ensure the animation works properly
    this.verticalLine.offsetHeight;

    if (isOpening) {
      // Hide vertical line (rotate to horizontal)
      this.verticalLine.style.transition = `transform ${this.config.animationDuration}ms ease-out, opacity ${this.config.animationDuration}ms ease-out`;
      this.verticalLine.style.transform = 'rotate(90deg)';
      this.verticalLine.style.opacity = '0';
    } else {
      // Show vertical line (rotate back to vertical)
      this.verticalLine.style.transition = `transform ${this.config.animationDuration}ms ease-out, opacity ${this.config.animationDuration}ms ease-out`;
      this.verticalLine.style.transform = 'rotate(0deg)';
      this.verticalLine.style.opacity = '1';
    }
  }

  /**
   * Animate the caret icon
   * @param {boolean} isOpening - Whether the accordion is opening
   * @private
   */
  animateCaretIcon(isOpening) {
    if (!this.caretLeft || !this.caretRight) return;

    // Force a reflow to ensure the animation works properly
    this.caretLeft.offsetHeight;

    if (isOpening) {
      // Rotate caret to point up (closed state)
      this.caretLeft.style.transition = `transform ${this.config.animationDuration}ms ease-out`;
      this.caretRight.style.transition = `transform ${this.config.animationDuration}ms ease-out`;
      this.caretLeft.style.transform = 'rotate(180deg)';
      this.caretRight.style.transform = 'rotate(180deg)';
    } else {
      // Rotate caret back to point down (open state)
      this.caretLeft.style.transition = `transform ${this.config.animationDuration}ms ease-out`;
      this.caretRight.style.transition = `transform ${this.config.animationDuration}ms ease-out`;
      this.caretLeft.style.transform = 'rotate(0deg)';
      this.caretRight.style.transform = 'rotate(0deg)';
    }
  }

  /**
   * Get current state
   * @returns {Object} Current state
   */
  getState() {
    return {
      isOpen: this.isOpen,
      config: this.config,
      elements: {
        trigger: this.elements.trigger,
        content: this.elements.content,
        icon: this.elements.icon
      }
    };
  }

  /**
   * Update configuration
   * @param {Object} newConfig - New configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    console.log('🎯 [AccordionElement] Configuration updated', this.config);
  }

  /**
   * Clean up component
   */
  destroy() {
    console.log('🎯 [AccordionElement] Destroying component');

    // Remove event listeners
    this.elements.trigger.removeEventListener('click', this.toggle);
    this.elements.trigger.removeEventListener('keydown', this.handleKeydown);

    // Remove classes
    this.classList.remove('is-open', 'is-closed', 'focused');
    
    // Dispatch destroy event
    this.dispatchEvent(new CustomEvent('accordion:destroyed', {
      detail: {
        element: this,
        timestamp: Date.now()
      },
      bubbles: true
    }));
  }
}

// Register custom element
if (!customElements.get('accordion-element')) {
  customElements.define('accordion-element', AccordionElement);
}

export default AccordionElement; 